import { Alert, Box, Typography, useTheme } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  GridRowParams,
  useGridApiRef,
} from "@mui/x-data-grid";
import { useEffect, useMemo } from "react";
import { useMatchesCityEscapeSummaries } from "../../hooks";
import { MatchFilter } from "../../libs/statsFilters";
import { formatSecondsToDDHHMMSS } from "../../libs/utils";
import { MatchCityEscapeSummary } from "../../services/matches";
import { formatNumber } from "../../utils/cityEscapeSummary";
import { useFirstColumnStickyStyles } from "../../hooks/datagrid.hooks";

/**
 * Definisce le colonne del DataGrid
 */
const createColumns = (): GridColDef[] => [
  {
    field: "teamName",
    headerName: "Team Name",
    width: 200,
  },
  {
    field: "playersNo",
    headerName: "Players No",
    width: 100,
  },
  {
    field: "cityEscapeName",
    headerName: "City Escape Title",
    width: 300,
    pinnable: true,
    sortable: true,
    filterable: false,
  },
  {
    field: "rating",
    headerName: "Rating",
    type: "number",
    sortable: true,
    filterable: false,
    valueFormatter: (value) =>
      value == null ? "N/D" : formatNumber(value || 0),
  },
  {
    field: "review",
    headerName: "Review",
    width: 400,
    sortable: false,
    filterable: false,
  },
  {
    field: "startedAt",
    headerName: "Start Date",
    width: 180,
    type: "dateTime",
    sortable: true,
    filterable: false,
    valueFormatter: (value) =>
      value ? new Date(value).toLocaleString() : "N/A",
  },
  {
    field: "endedAt",
    headerName: "End Date",
    width: 180,
    type: "dateTime",
    sortable: true,
    filterable: false,
    valueFormatter: (value) =>
      value ? new Date(value).toLocaleString() : "N/A",
  },
  {
    field: "duration",
    headerName: "Duration",
    width: 130,
    type: "number",
    sortable: true,
    filterable: false,
    valueFormatter: (value) => formatSecondsToDDHHMMSS(value || 0),
  },
  {
    field: "digital",
    headerName: "Digital",
    type: "boolean",
    width: 120,
    sortable: true,
    filterable: false,
  },
  {
    field: "propsWithdrawn",
    headerName: "Props Withdrawn",
    type: "boolean",
    sortable: true,
    filterable: false,
    width: 150,
  },
  {
    field: "totalEnigmas",
    headerName: "Total Enigmas",
    type: "number",
    width: 130,
    sortable: true,
    filterable: false,
  },
  {
    field: "avgEnigmaTime",
    headerName: "Avg Enigma Time",
    width: 150,
    type: "number",
    sortable: true,
    filterable: false,
    valueFormatter: (value) => formatSecondsToDDHHMMSS(value || 0),
  },
  {
    field: "avgHintUsage",
    headerName: "Avg Hint Usage",
    width: 150,
    type: "number",
    sortable: true,
    filterable: false,
    valueFormatter: (value) => formatNumber(value || 0),
  },
  {
    field: "avgErrors",
    headerName: "Avg Errors",
    width: 150,
    type: "number",
    sortable: true,
    filterable: false,
    valueFormatter: (value) => formatNumber(value || 0),
  },
  {
    field: "fulfilled",
    headerName: "Fulfilled",
    type: "boolean",
    width: 120,
    sortable: true,
    filterable: false,
  },
];
/**
 * Componente LoadingState separato per chiarezza
 */
const LoadingState: React.FC = () => (
  <Box display="flex" justifyContent="center" alignItems="center" height={400}>
    <Typography>Loading matches summaries...</Typography>
  </Box>
);

/**
 * Componente ErrorState separato per chiarezza
 */
const ErrorState: React.FC<{ error: string }> = ({ error }) => (
  <Alert severity="error" sx={{ margin: 2 }}>
    Error loading data: {error}
  </Alert>
);

/**
 * Componente principale CityEscapeTable
 */
export const MatchesTable = ({
  cityEscapeId,
  filters,
  onRowDoubleClick,
}: {
  cityEscapeId?: string;
  filters?: MatchFilter;
  onRowDoubleClick?: (row: GridRowParams<MatchCityEscapeSummary>) => void;
}) => {
  const summaries = useMatchesCityEscapeSummaries({
    cityEscapeIds: cityEscapeId,
    filters,
  });
  const apiRef = useGridApiRef();

  useEffect(() => {
    if (apiRef.current) {
      console.log(apiRef.current.state.rows);
    }
  }, [apiRef.current?.state.rows]);

  // console.warn("Pagination Model:", paginationModel);s

  const columns = useMemo(() => createColumns(), []);
  const firstColumnStickyStyles = useFirstColumnStickyStyles();

  if (summaries.isLoading) {
    return <LoadingState />;
  }

  if (summaries.error) {
    return <ErrorState error={summaries.error?.message} />;
  }

  return (
    <Box>
      <DataGrid
        apiRef={apiRef}
        rows={summaries.data || []}
        disableVirtualization
        // onSortModelChange={}
        onStateChange={(state: GridEventListener<"stateChange">) => {
          state;
        }}
        onRowDoubleClick={onRowDoubleClick}
        columns={columns}
        initialState={{
          columns: {
            columnVisibilityModel: {},
          },
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
          sorting: {
            sortModel: [{ field: "totalMatches", sort: "desc" }],
          },
        }}
        pageSizeOptions={[5, 10, 20, 50]}
        // disableRowSelectionOnClick
        sx={firstColumnStickyStyles}
      />
    </Box>
  );
};

export default MatchesTable;
